import { useState, useCallback, type DragEvent, type ChangeEvent } from 'react'
import { PageHead } from '@components/PageHead'
import { ToolsSubNav } from '@features/tools/components/ToolsSubNav'
import { useFolders } from '@features/vault/hooks/useFolders'
import { ImportError, type ImportPreview } from '@features/tools/types'
import { executeImport, parseImportFile } from '@features/tools/services/importFlow'
import { unzip } from '@features/tools/services/unzip'
import { Notice } from '@shared/ui/Notice'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * インポート画面 (F3、Phase 5)。
 *
 * 流れ:
 *   1. drag&drop / file input で JSON ファイルを受領
 *   2. `parseImportFile` で OoKey native / Bitwarden を判定 → ImportPreview
 *   3. preview 一覧を表示 → ユーザー確認 → `executeImport` で bulk create
 *
 * Bitwarden encrypted JSON / 1Password / CSV / KeePass は別 batch で defer。
 */

type Phase =
  | { kind: 'idle' }
  | { kind: 'parsing' }
  | { kind: 'preview'; preview: ImportPreview }
  | { kind: 'importing'; preview: ImportPreview }
  | { kind: 'done'; created: number; failed: number; foldersCreated: number; skipped: number }
  | { kind: 'error'; messageKey: string }

export const ImportPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { folders } = useFolders()
  const { show: showToast } = useToast()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [dragOver, setDragOver] = useState(false)

  const handleFileText = useCallback(
    (text: string): void => {
      try {
        const preview = parseImportFile(text, folders)

        setPhase({ kind: 'preview', preview })
      } catch (e) {
        const messageKey =
          e instanceof ImportError
            ? `tools.import.error.${e.messageKey}`
            : 'tools.import.error.generic'

        setPhase({ kind: 'error', messageKey })
      }
    },
    [folders],
  )

  const onFileSelected = useCallback(
    async (file: File): Promise<void> => {
      setPhase({ kind: 'parsing' })
      try {
        // .zip は展開し、中の JSON (無損失) を優先、無ければ CSV を取り込む。
        if (/\.zip$/iu.test(file.name)) {
          const files = await unzip(new Uint8Array(await file.arrayBuffer()))
          const names = Object.keys(files)
          const target = names.find(n => /\.json$/iu.test(n)) ?? names.find(n => /\.csv$/iu.test(n))
          const data = target !== undefined ? files[target] : undefined

          if (data === undefined) {
            setPhase({ kind: 'error', messageKey: 'tools.import.error.zip_no_data' })

            return
          }
          handleFileText(new TextDecoder().decode(data))

          return
        }

        handleFileText(await file.text())
      } catch {
        setPhase({ kind: 'error', messageKey: 'tools.import.error.read_failed' })
      }
    },
    [handleFileText],
  )

  const onFileInputChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>): void => {
      const file = ev.target.files?.[0]

      if (file) void onFileSelected(file)
    },
    [onFileSelected],
  )

  const onDrop = useCallback(
    (ev: DragEvent<HTMLDivElement>): void => {
      ev.preventDefault()
      setDragOver(false)

      const file = ev.dataTransfer.files?.[0]

      if (file) void onFileSelected(file)
    },
    [onFileSelected],
  )

  const onDragOver = useCallback((ev: DragEvent<HTMLDivElement>): void => {
    ev.preventDefault()
    setDragOver(true)
  }, [])

  const onDragLeave = useCallback((): void => {
    setDragOver(false)
  }, [])

  const onConfirmImport = useCallback(async (): Promise<void> => {
    if (phase.kind !== 'preview') return
    const preview = phase.preview

    setPhase({ kind: 'importing', preview })
    try {
      const result = await executeImport(preview, folders)

      setPhase({
        kind: 'done',
        created: result.created,
        failed: result.failed,
        foldersCreated: result.foldersCreated,
        skipped: result.skipped,
      })
      showToast({
        titleKey: 'tools.import.success',
        titleVars: { count: result.created },
        tone: 'success',
        dedupe: true,
      })
    } catch {
      setPhase({ kind: 'error', messageKey: 'tools.import.error.generic' })
    }
  }, [phase, folders, showToast])

  const reset = (): void => {
    setPhase({ kind: 'idle' })
  }

  return (
    <div>
      <PageHead
        title={t('tools.import.title')}
        sub={t('tools.import.sub')}
        beforeTabs={<ToolsSubNav active="tools/import" />}
      />
      <div className="page-body">
        <Notice tone="info" ico="info">
          {t('tools.import.intro')}
        </Notice>

        {(phase.kind === 'idle' || phase.kind === 'parsing' || phase.kind === 'error') && (
          <div className="card gen-form-card">
            <div className="field">
              <label className="field-label">{t('tools.import.format_label')}</label>
              <div className="field-help">{t('tools.import.format_hint')}</div>
            </div>

            <div
              className={`gen-drop-zone${dragOver ? ' drag-over' : ''}`}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              data-testid="import-drop-zone"
            >
              <div className="gen-drop-zone-icon" aria-hidden="true">
                <Icon name="upload" />
              </div>
              <div className="gen-drop-zone-label">{t('tools.import.file_drop')}</div>
              <div className="gen-drop-zone-hint">{t('tools.import.file_hint')}</div>
              <label className="btn btn-secondary import-file-button">
                <Icon name="upload" aria-hidden="true" />
                {t('tools.import.file_select')}
                <input
                  type="file"
                  accept="application/json,.json,text/csv,.csv,application/zip,.zip"
                  onChange={onFileInputChange}
                  className="import-file-input-hidden"
                  data-testid="import-file-input"
                />
              </label>
            </div>

            {phase.kind === 'parsing' && (
              <Notice tone="info" ico="info">
                {t('tools.import.parsing')}
              </Notice>
            )}

            {phase.kind === 'error' && (
              <Notice tone="danger" ico="alert">
                {t(phase.messageKey)}
              </Notice>
            )}
          </div>
        )}

        {(phase.kind === 'preview' || phase.kind === 'importing') && (
          <div className="card gen-form-card import-preview-card">
            <h3 className="settings-card-title">
              {t('tools.import.preview_title', { count: phase.preview.items.length })}
            </h3>
            <div className="field-help">
              {t(`tools.import.format_detected_${phase.preview.format}`)}
            </div>

            {phase.preview.newFolderNames.length > 0 && (
              <Notice tone="info" ico="folder">
                {t('tools.import.new_folders_notice', {
                  count: phase.preview.newFolderNames.length,
                })}
              </Notice>
            )}

            <ul className="import-preview-list">
              {phase.preview.items.slice(0, 30).map((it, i) => (
                <li key={i} className="import-preview-row">
                  <div className="import-preview-name">{it.name}</div>
                  <div className="import-preview-type">{t(`vault.type.${it.payload.type}`)}</div>
                </li>
              ))}
              {phase.preview.items.length > 30 && (
                <li className="import-preview-more">
                  {t('tools.import.preview_more', {
                    count: phase.preview.items.length - 30,
                  })}
                </li>
              )}
            </ul>

            <div className="gen-form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={reset}
                disabled={phase.kind === 'importing'}
              >
                {t('tools.import.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void onConfirmImport()}
                disabled={phase.kind === 'importing'}
                data-testid="import-confirm"
              >
                <Icon name="check" aria-hidden="true" />
                {phase.kind === 'importing'
                  ? t('tools.import.running')
                  : t('tools.import.confirm', { count: phase.preview.items.length })}
              </button>
            </div>
          </div>
        )}

        {phase.kind === 'done' && (
          <div className="card gen-form-card">
            <Notice tone="info" ico="check">
              {t('tools.import.done', {
                count: phase.created,
                folders: phase.foldersCreated,
              })}
            </Notice>
            {phase.failed > 0 && (
              <Notice tone="warn" ico="alert">
                {t('tools.import.partial_failure', { count: phase.failed })}
              </Notice>
            )}
            {phase.skipped > 0 && (
              <Notice tone="info" ico="info">
                {t('tools.import.skipped_duplicates', { count: phase.skipped })}
              </Notice>
            )}
            <div className="gen-form-actions">
              <button type="button" className="btn btn-secondary" onClick={reset}>
                {t('tools.import.again')}
              </button>
              <a className="btn btn-primary" href="#vault">
                <Icon name="folder" aria-hidden="true" />
                {t('tools.import.go_vault')}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
